// @ts-strict-ignore
import type {
  DashboardApiEndpointDefinitions,
  dashboardApiEndpoints,
  FilteredDashboardApiEndpointKeys,
  UseDashboardApiInfiniteQueryProps,
} from "@dashboardApi/apis";
import { useInfiniteQuery } from "@tanstack/react-query";

import type { Awaited } from "@lib/typeHelpers";

import { useAppGroupId } from "@services/useAppGroupId";

import { useDashboardApi } from "./useDashboardApi";

/**
 * Based on the endpoint specified, this accepts arguments for the underlying GET fetcher function,
 * and options for react-query, and converts them into the query key and arguments to useInfiniteQuery
 * @template Endpoint the endpoint name. Example: `"/engagement/get_changelogs"`
 * @template GetFetcherFunction an intermediary type to remove duplication in the type, this is the type of the fetcher function for `GET`
 * @template TData this is the return value of `select`, which is inferred from the `options` argument.
 */
export const useDashboardApiInfiniteQuery = <
  Endpoint extends FilteredDashboardApiEndpointKeys,
  GetFetcherFunction extends DashboardApiEndpointDefinitions[Endpoint]["GET"],
  TFnData = Awaited<ReturnType<(typeof dashboardApiEndpoints)[Endpoint]["GET"]>>,
  TData = TFnData,
>(
  endpoint: Endpoint,
  {
    params,
    options,
  }: UseDashboardApiInfiniteQueryProps<typeof dashboardApiEndpoints, Endpoint, GetFetcherFunction, TFnData, TData>
) => {
  const appGroupId = useAppGroupId();
  const { GET } = useDashboardApi(endpoint);
  const queryKey: Parameters<GetFetcherFunction>[0]["queryKey"] = [
    { cacheKey: endpoint, appGroupId },
    params || [],
  ] as unknown as Parameters<GetFetcherFunction>[0]["queryKey"];
  return useInfiniteQuery(queryKey, GET as any, options);
};
